import { Box, SxProps, Theme } from "@mui/material";
import { ReactNode } from "react";
import {
  FONT_DISPLAY,
  FONT_MONO,
  FONT_TEXT,
  FONT_UI,
  HEADING_SIZE,
  STICKY_TOP,
} from "@/src/components/common/broadsheet/tokens";

export default function Prose({
  children,
  id,
  sx,
}: {
  children: ReactNode;
  id?: string;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box
      id={id}
      sx={[
        {
          fontFamily: FONT_TEXT,
          fontSize: { xs: "0.9375rem", md: "1rem" },
          lineHeight: 1.7,
          color: "custom.inkMuted",

          "& > *:first-of-type": { mt: 0 },
          "& > *:last-child": { mb: 0 },

          "& h1, & h2, & h3, & h4, & h5, & h6": {
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            color: "custom.ink",
            letterSpacing: "0.01em",
            lineHeight: 1.15,
            scrollMarginTop: STICKY_TOP,
            mt: 1.75,
            mb: 0.5,
          },
          "& h1": { fontSize: HEADING_SIZE.page },
          "& h2": {
            fontSize: HEADING_SIZE.section,
            pb: 0.3125,
            borderBottom: "1px solid",
            borderColor: "custom.ruleHairline",
          },
          "& h3": { fontSize: HEADING_SIZE.subsection },
          "& h4, & h5, & h6": {
            fontFamily: FONT_UI,
            fontSize: "0.8125rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "custom.kicker",
          },

          "& p": { m: 0, mb: 1 },

          "& a": {
            color: "custom.link",
            textDecoration: "underline",
            textUnderlineOffset: "0.15em",
            /* A bare URL pasted as link text otherwise forces horizontal
             * scroll on narrow screens. */
            overflowWrap: "anywhere",
            "&:hover": { color: "custom.linkHover" },
          },

          "& strong, & b": { fontWeight: 700 },

          "& ul, & ol": { m: 0, mb: 1, pl: 1.25 },
          "& li": { mb: 0.25 },
          "& li::marker": { color: "custom.kicker" },

          "& blockquote": {
            m: 0,
            mb: 1,
            pl: 1,
            py: 0.25,
            borderLeft: "3px solid",
            borderColor: "primary.main",
            fontStyle: "italic",
            color: "custom.inkMuted",
          },

          "& hr": {
            border: 0,
            borderTop: "1px solid",
            borderColor: "custom.ruleHairline",
            my: 1.5,
          },

          "& img": {
            maxWidth: "100%",
            height: "auto",
            display: "block",
          },

          "& figure": { m: 0, mb: 1 },
          "& figcaption": {
            fontFamily: FONT_UI,
            fontSize: "0.75rem",
            color: "custom.subtitleText",
            mt: 0.25,
          },

          "& table": {
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: FONT_UI,
            fontSize: "0.8125rem",
            mb: 1,
            backgroundColor: "custom.panelSurface",
          },
          "& thead th": {
            textAlign: "left",
            fontWeight: 700,
            color: "custom.ink",
            borderBottom: "2px solid",
            borderColor: "custom.ruleMasthead",
            p: 0.5,
          },
          "& tbody td, & tbody th": {
            borderBottom: "1px solid",
            borderColor: "custom.ruleHairline",
            p: 0.5,
            verticalAlign: "top",
          },

          "& code, & pre": {
            fontFamily: FONT_MONO,
            fontSize: "0.8125rem",
            backgroundColor: "custom.newsprintRaised",
          },
          "& code": { px: 0.25, py: 0.0625 },
          "& pre": {
            p: 0.75,
            overflowX: "auto",
            border: "1px solid",
            borderColor: "custom.ruleHairline",
          },

          "& iframe": { border: 0, maxWidth: "100%" },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}
